import React, { useContext, useEffect } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useRouter } from 'next/router'

import { ArticleCategory } from '@ecm/data/ArticleCategory'
import { DropdownContext } from '@ecm/pages/_app'
import Anchor from '@ecm/ui/element/Anchor'
import CloudflareImage from '@ecm/ui/element/CloudflareImage'
import Color from '@ecm/ui/element/Color'

import expandIcon from '/public/images/icon/expand.svg'
import expandIconWhite from '/public/images/icon/expandWhite.svg'

type SharedTypes = {
  currentCategorySlug: string
}

type CategoriesLinksProps = {
  categories: ArticleCategory[]
  className?: string
} & SharedTypes

type CategoryItemProps = {
  category?: ArticleCategory
  slug: string
  link: string
} & SharedTypes

const CategoryItem = ({ currentCategorySlug, category, slug, link }: CategoryItemProps) => {
  const { handleDropdownChange } = useContext(DropdownContext)

  const isActive = currentCategorySlug === slug

  return (
    <Grid item xs={12} sm={6} md={4} lg={3}>
      <Box
        bgcolor={isActive ? Color.saint.normal : Color.white.normal}
        border={`1px solid ${Color.smoke.darkest}`}
        borderRadius="4px"
        textAlign="center"
        onClick={() => handleDropdownChange(false)}
      >
        <Anchor
          className="category-button"
          href={link}
          scroll={false}
          sx={
            isActive
              ? {
                  color: Color.white.normal,
                  cursor: 'default',
                }
              : {
                  '&:hover': { color: Color.mono.lighter },
                  color: Color.saint.normal,
                }
          }
        >
          {slug === 'all' ? 'All' : category?.name}
        </Anchor>
      </Box>
    </Grid>
  )
}

export const CategoriesLinks = styled(
  ({ categories, currentCategorySlug, className }: CategoriesLinksProps) => {
    const router = useRouter()
    const theme = useTheme()
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
    const { isDropdownExpanded, handleCategoryChange, handleDropdownChange, pickedCategory } =
      useContext(DropdownContext)

    const isCategoryPicked = pickedCategory && !isDropdownExpanded

    useEffect(() => {
      if (categories && router.pathname.includes('/blog')) {
        const categoryName = categories.find(c => c.slug === currentCategorySlug)?.name
        handleCategoryChange(categoryName)
      }
    })

    return (
      <Box
        className={className}
        padding={{ lg: '4em 0', md: '3em 0', sm: '0 0 4em', xs: '0 0 3em' }}
      >
        <Grid
          container
          /* eslint-disable sort-keys */
          columnSpacing={{
            sm: 3,
            md: 2,
            lg: 4,
            xl: 6,
          }}
          /* eslint-disable sort-keys */
          rowSpacing={3}
          mt={{ md: '1.5em', xs: '0.5em' }}
        >
          {isSmallScreen ? (
            <Accordion
              className="drop-down"
              onChange={() => handleDropdownChange()}
              expanded={isDropdownExpanded}
              sx={{
                backgroundColor: isCategoryPicked ? Color.saint.normal : Color.white.normal,
              }}
            >
              <AccordionSummary
                aria-controls="categories-content"
                id="categories-header"
                expandIcon={
                  <CloudflareImage src={isCategoryPicked ? expandIconWhite : expandIcon} />
                }
              >
                <Typography
                  variant="p2"
                  my="0.5em"
                  color={isCategoryPicked ? Color.white.normal : Color.saint.darker}
                >
                  {pickedCategory ? pickedCategory : 'All categories'}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <CategoryItem slug="all" link="/blog" currentCategorySlug={currentCategorySlug} />
              </AccordionDetails>
              {categories.map(category => (
                <AccordionDetails key={category.id}>
                  <CategoryItem
                    slug={category.slug}
                    category={category}
                    link={`/blog/category/${category.slug}`}
                    currentCategorySlug={currentCategorySlug}
                  />
                </AccordionDetails>
              ))}
            </Accordion>
          ) : (
            <>
              <CategoryItem slug="all" link="/blog" currentCategorySlug={currentCategorySlug} />
              {categories.map(category => (
                <CategoryItem
                  key={category.id}
                  slug={category.slug}
                  category={category}
                  link={`/blog/category/${category.slug}`}
                  currentCategorySlug={currentCategorySlug}
                />
              ))}
            </>
          )}
        </Grid>
      </Box>
    )
  }
)({
  '.category-button': {
    borderRadius: '4px',
    fontWeight: 'bold',
    margin: '0',
    padding: '0.75em 0',
    display: 'inline-block',
    width: '100%',
  },

  '.drop-down': {
    border: `1px solid ${Color.smoke.darkest}`,
    borderRadius: '4px',
    boxShadow: 'none',
    width: '100%',
    '*, *::before, *::after': {
      transition: 'none !important',
      animation: 'none !important',
    },
  },
})

export default { Categories: CategoriesLinks }
